import { useState } from 'react';
import { DeleteProfileAction } from '@/components/common/DeleteProfileAction';
import { ProfileCardGrid, ProfileGridItem } from '@/components/common/ProfileCardGrid';
import { ProfileLayout } from '@/components/common/ProfileLayout';
import { SectionEditButton } from '@/components/common/SectionEditButton';
import { ProfileSetupPrompt } from '@/components/common/ProfileSetupPrompt';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import type { InvestorProfileData } from '../../api/investor-profile.api';
import { useDeleteInvestorProfile } from './useInvestorProfileMutations';
import { InvestorProfileHero } from './InvestorProfileHero';
import { InvestorSectionEditor } from './InvestorSectionEditor';
import { InvestorMissingFieldsNotice } from './InvestorMissingFieldsNotice';
import { INVESTOR_SECTION_VIEWS } from './InvestorSectionContent';
import {
  INVESTOR_SECTION_ORDER,
  INVESTOR_SECTIONS,
  type InvestorEditorTarget,
  type InvestorSectionId,
} from './investor-sections';

const S = UI_STRINGS.INVESTOR_PROFILE;

interface InvestorProfileSectionsProps {
  data: InvestorProfileData;
  readOnly: boolean;
  entityId: string;
}

/** Delete Profile at the foot of the page (US-042), moved out of the Geographies form. */
function InvestorDeleteAction({ entityId }: { entityId: string }) {
  const remove = useDeleteInvestorProfile(entityId);
  return (
    <DeleteProfileAction
      label={S.DELETE_PROFILE}
      confirmTitle={S.DELETE_CONFIRM_TITLE}
      confirmBody={S.DELETE_CONFIRM_BODY}
      confirmLabel={S.DELETE_CONFIRM_ACTION}
      isDeleting={remove.isPending}
      onDelete={() => remove.mutate()}
    />
  );
}

function InvestorCards({
  data,
  readOnly,
  onOpen,
}: {
  data: InvestorProfileData;
  readOnly: boolean;
  onOpen: (section: InvestorEditorTarget) => void;
}) {
  return (
    <ProfileCardGrid>
      {INVESTOR_SECTION_ORDER.map((id) => (
        <ProfileGridItem key={id} span={INVESTOR_SECTIONS[id].span}>
          {INVESTOR_SECTION_VIEWS[id]({
            data,
            action: readOnly ? undefined : (
              <SectionEditButton section={INVESTOR_SECTIONS[id].title} onClick={() => onOpen(id)} />
            ),
            onAdd: readOnly ? undefined : () => onOpen(id),
          })}
        </ProfileGridItem>
      ))}
    </ProfileCardGrid>
  );
}

/**
 * The investor profile module's `Sections` component (ADR FE-1 registry): the
 * header plus one card per section, each edited in its own side panel
 * (MYN-217, profile-redesign FR-164 to FR-169, FR-174, FR-175). A profile that
 * does not exist yet shows the one-panel setup prompt instead (FR-181). An editor
 * also sees which fields are still missing for matching, as the Backend reports
 * them, each opening its panel (MYN-278, FR-186); a read-only viewer sees none.
 */
export function InvestorProfileSections({
  data,
  readOnly,
  entityId,
}: InvestorProfileSectionsProps) {
  const [openSection, setOpenSection] = useState<InvestorEditorTarget | null>(null);
  // No profile yet: the Backend answers with a shell (`id: null`) and the first save creates it.
  const isNew = !data.id;
  // Before a profile exists the setup panel is the only one that holds any field.
  const openField = (id: InvestorSectionId) => setOpenSection(isNew ? 'setup' : id);

  return (
    <ProfileLayout hero={<InvestorProfileHero data={data} />}>
      {!readOnly && (
        <InvestorMissingFieldsNotice missingFields={data.matchingMissingFields} onOpen={openField} />
      )}
      {isNew ? (
        <ProfileSetupPrompt onStart={readOnly ? undefined : () => setOpenSection('setup')} />
      ) : (
        <InvestorCards data={data} readOnly={readOnly} onOpen={setOpenSection} />
      )}
      {!readOnly && (
        <>
          {!isNew && <InvestorDeleteAction entityId={entityId} />}
          <InvestorSectionEditor
            data={data}
            entityId={entityId}
            section={openSection}
            onClose={() => setOpenSection(null)}
          />
        </>
      )}
    </ProfileLayout>
  );
}
